import React, { useState, useEffect, useCallback } from "react";
import { useBooking } from "./BookingContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const Step4 = () => {
  const { bookingData, updateBookingData } = useBooking();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Memoize updateBookingData to prevent unnecessary re-renders
  const memoizedUpdateBookingData = useCallback((key, value) => {
    updateBookingData(key, value);
  }, []);

  // Calculate initial dates only once when plan changes
  useEffect(() => {
    if (bookingData.plan && !startDate) {
      // Only run if startDate isn't set
      const today = new Date();
      const todayStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );
      setStartDate(todayStart);

      let calculatedEndDate = new Date(todayStart);

      switch (bookingData.plan) {
        case "daily":
          calculatedEndDate.setDate(todayStart.getDate() + 1);
          break;
        case "weekly":
          calculatedEndDate.setDate(todayStart.getDate() + 7);
          break;
        case "monthly":
          calculatedEndDate.setMonth(todayStart.getMonth() + 1);
          break;
        default:
          calculatedEndDate.setDate(todayStart.getDate() + 1);
      }

      setEndDate(calculatedEndDate);

      // Update context
      memoizedUpdateBookingData("startDate", todayStart);
      memoizedUpdateBookingData("endDate", calculatedEndDate);
    }
  }, [bookingData.plan, memoizedUpdateBookingData, startDate]); // Add startDate to dependencies

  const handleStartDateChange = useCallback(
    (date) => {
      if (!date) return;

      setStartDate(date);

      // Recalculate end date based on new start date
      let newEndDate = new Date(date);

      switch (bookingData.plan) {
        case "daily":
          newEndDate.setDate(date.getDate() + 1);
          break;
        case "weekly":
          newEndDate.setDate(date.getDate() + 7);
          break;
        case "monthly":
          newEndDate.setMonth(date.getMonth() + 1);
          break;
        default:
          newEndDate.setDate(date.getDate() + 1);
      }

      setEndDate(newEndDate);

      // Update context
      memoizedUpdateBookingData("startDate", date);
      memoizedUpdateBookingData("endDate", newEndDate);
    },
    [bookingData.plan, memoizedUpdateBookingData]
  );

  const formatDateForInput = useCallback((date) => {
    if (!date) return "";
    return date.toISOString().split("T")[0];
  }, []);

  // Update HTML inputs for jQuery validation
  useEffect(() => {
    if (startDate) {
      const startInput = document.querySelector('input[name="startdate"]');
      if (startInput) {
        startInput.value = formatDateForInput(startDate);
      }
    }

    if (endDate) {
      const endInput = document.querySelector('input[name="enddate"]');
      if (endInput) {
        endInput.value = formatDateForInput(endDate);
      }
    }
  }, [startDate, endDate, formatDateForInput]);

  return (
    <div className="multisteps-form__panel" data-animation="slideHorz">
      <div className="wizard-forms">
        <div className="inner pb-100 clearfix cont">
          <div className="form-content pera-content">
            <div className="step-inner-content">
              <span className="step-no bottom-line">Step 4</span>
              <div className="step-progress float-end">
                <span>4 of 5 completed</span>
                <div className="step-progress-bar">
                  <div className="progress">
                    <div
                      className="progress-bar"
                      style={{ width: "70%" }}
                    ></div>
                  </div>
                </div>
              </div>
              <h2>Choose Date Range</h2>
              <p>
                Finally, please choose a start and end date to enable us make
                reservations for you.
              </p>
            <p className="text-info" style={{ backgroundColor: "#8a038c", color: "white", }}>
                Selected Plan:{" "}
                <strong>
                  {bookingData.plan
                    ? bookingData.plan.charAt(0).toUpperCase() +
                      bookingData.plan.slice(1)
                    : "Daily"}
                </strong>
              </p>
              <div className="step-content-field">
                <div className="date-picker date datepicker">
                  <DatePicker
                    selected={startDate}
                    onChange={handleStartDateChange}
                    minDate={new Date()}
                    dateFormat="yyyy-MM-dd"
                    className="form-control"
                    id="startDate"
                    placeholderText="Select start date"
                  />
                  <label>START DATE</label>
                </div>
                {/* <div className="date-picker date datepicker">
                  <input
                    type="text"
                    name="startdate"
                    className="form-control"
                    id="startDate"
                  />
                  <div className="input-group-append">
                    <span>START DATE</span>
                  </div>
                </div> */}
                {/* <div className="date-picker date datepicker">
                  <input
                    type="text"
                    name="enddate"
                    className="form-control"
                    id="endDate"
                    readOnly
                  />
                  <div className="input-group-append">
                    <span>END DATE</span>
                  </div>
                </div> */}

                <div className="date-picker date datepicker">
                   <input
                    type="text"
                    name="enddate"
                    className="form-control"
                    id="endDate"
                    readOnly
                    value={endDate ? formatDateForInput(endDate) : ""}
                  />
                  <label>END DATE (Auto-calculated)</label>
                </div>

                {/* Hidden inputs for jQuery validation */}
                <input 
                  type="hidden" 
                  name="startdate" 
                  value={startDate ? formatDateForInput(startDate) : ""}
                />
                <input 
                  type="hidden" 
                  name="enddate" 
                  value={endDate ? formatDateForInput(endDate) : ""}
                />

                {startDate && endDate && (
                  <div className="date-summary mt-3 p-3 bg-light rounded">
                    <h5>Booking Summary:</h5>
                    <p>Start: {startDate.toDateString()}</p>
                    <p>End: {endDate.toDateString()}</p>
                    <p>
                      Duration:{" "}
                      {bookingData.plan === "daily"
                        ? "1 Day"
                        : bookingData.plan === "weekly"
                        ? "1 Week"
                        : bookingData.plan === "monthly"
                        ? "1 Month"
                        : "Custom"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="actions">
          <ul>
            <li>
              <span className="js-btn-prev" title="BACK">
                <i className="fa fa-arrow-left"></i> BACK{" "}
              </span>
            </li>
            <li>
              <span className="js-btn-next" title="NEXT">
                NEXT <i className="fa fa-arrow-right"></i>
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
export default Step4;
